import React from 'react';
import { 
  AlertTitle,
  IconButton,
  Collapse
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { StyledAlert } from './Alert.style';

export interface AlertProps extends Omit<import('@mui/material').AlertProps, 'variant'> {
  /**
   * The severity of the alert
   */
  severity?: 'error' | 'warning' | 'info' | 'success';
  /**
   * The variant of the alert
   */
  variant?: 'filled' | 'outlined' | 'standard';
  /**
   * The title of the alert
   */
  title?: string;
  /**
   * The content of the alert
   */
  children?: React.ReactNode;
  /**
   * Whether the alert can be closed
   */
  closable?: boolean;
  /**
   * Whether the alert is visible
   */
  open?: boolean;
  /**
   * Callback fired when the alert is closed
   */
  onClose?: () => void;
  /**
   * Custom icon for the alert
   */
  icon?: React.ReactNode;
  /**
   * Whether to show the default icon
   */
  showIcon?: boolean;
  /**
   * Custom action buttons
   */
  action?: React.ReactNode;
}

export const Alert: React.FC<AlertProps> = ({
  severity = 'info',
  variant = 'standard',
  title,
  children,
  closable = false,
  open = true,
  onClose,
  icon,
  showIcon = true,
  action,
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(open);

  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const closeAction = closable ? (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={handleClose}
    >
      <Close fontSize="inherit" />
    </IconButton>
  ) : undefined;

  const alertAction = action || closeAction;

  return (
    <Collapse in={isOpen}>
      <StyledAlert
        severity={severity}
        variant={variant}
        icon={showIcon ? icon : false}
        action={alertAction}
        {...props}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </StyledAlert>
    </Collapse>
  );
}; 